import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useMemo, useEffect, useRef } from 'react'
import { ArrowLeft } from 'lucide-react'
import { ActionBanner } from '@/components/ActionBanner'
import {
  deriveCollaborationState,
  getAthleteStatusDisplay,
  type Collaboration as CollabStateType,
  type Milestone as MilestoneStateType,
} from '@adthlete/common/utils/collaborationState.ts'
import {
  getAthleteRouteForMilestone,
  getMilestoneByName,
} from '@adthlete/common/utils/milestoneConfig'
import { useNavigate } from 'react-router-dom'
import { useCampaignDetail } from '@/hooks/useCampaignDetail'
import { useGetVideoSuggestionsQuery, useGetCreativeIdeasQuery } from '@/redux/collaborationsApi'

// Components
import CollaborationHeader from '@/components/collaboration-detail/CollaborationHeader'
import CollaborationStatusCard from '@/components/collaboration-detail/CollaborationStatusCard'
import CollaborationDetailTabs from '@/components/collaboration-detail/CollaborationDetailTabs'
import PaymentInfoCard from '@/components/collaboration-detail/PaymentInfoCard'

// Hooks
import { useFeedbackMessages } from '@/hooks/useFeedbackMessages'

const CollaborationDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // State
  const [searchParams, setSearchParams] = useSearchParams()

  const { campaign, isLoading: detailLoading } = useCampaignDetail(id)

  const { data: videoSuggestionsData } = useGetVideoSuggestionsQuery(id!, { skip: !id })
  const hasVideoSuggestion = (videoSuggestionsData?.suggestions?.length ?? 0) > 0

  const { data: creativeIdeasData } = useGetCreativeIdeasQuery(id!, { skip: !id })
  const hasConceptSuggestion =
    (creativeIdeasData?.ideas?.filter((idea) => idea.createdByTeam !== null).length ?? 0) > 0

  // Tab logic
  const validTabs = useMemo(() => {
    const tabs = ['feedback', 'details', 'documents', 'events']
    if (campaign?.complianceNeeded) tabs.splice(1, 0, 'compliance')
    return tabs
  }, [campaign?.complianceNeeded])
  const tabFromUrl = searchParams.get('tab')
  const activeTab = validTabs.includes(tabFromUrl || '') ? tabFromUrl || 'feedback' : 'feedback'
  const highlightEventId = searchParams.get('eventId') ?? undefined

  // When navigating to the events or compliance tab, scroll tabs into view
  const tabsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if ((activeTab === 'events' || activeTab === 'compliance') && tabsRef.current) {
      setTimeout(() => {
        tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 150)
    }
  }, [activeTab])

  const handleTabChange = (newTab: string) => {
    if (validTabs.includes(newTab)) {
      setSearchParams({ tab: newTab }, { preventScrollReset: true })
    }
  }

  // Logic - Collaboration State
  const collaborationState = useMemo(() => {
    if (!campaign) return null
    const collabForState: CollabStateType = {
      status: campaign.status as CollabStateType['status'],
      milestones: (campaign.milestones || []).map((m: any) => ({
        id: m.id,
        name: m.name,
        status: m.status as MilestoneStateType['status'],
        requiresApproval: m.requiresApproval,
        sequenceOrder: m.sequenceOrder,
        submissions: (m.submissions || []).map((s: any) => ({
          id: s.id,
          status: s.status,
        })),
      })),
    }
    return deriveCollaborationState(collabForState)
  }, [campaign])

  const statusDisplay = useMemo(() => {
    if (!collaborationState) return null
    return getAthleteStatusDisplay(collaborationState)
  }, [collaborationState])

  const isLiveAndPublishedComplete = useMemo(() => {
    if (!campaign?.milestones?.length) return false
    const liveM = campaign.milestones.find(
      (m: any) => getMilestoneByName(m.name)?.slug === 'collaboration_live',
    )
    if (!liveM) return campaign.status === 'completed'
    return liveM.status === 'completed' || liveM.status === 'approved'
  }, [campaign])

  const handleActionBannerClick = () => {
    if (!campaign?.id) return

    // Special case for 'offered' status
    if (campaign.status === 'offered') {
      navigate(`/bids/${id}`) // id is already encoded from URL params
      return
    }

    if (!collaborationState?.currentMilestone) return
    const link = getAthleteRouteForMilestone(collaborationState.currentMilestone.name, campaign.id)
    if (link) {
      navigate(link)
    }
  }

  // Logic - Feedback Messages
  const feedbackMessages = useFeedbackMessages(campaign)

  if (!campaign && !detailLoading) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4">Collaboration Not Found</h2>
        <p className="mb-4">The collaboration you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-500 hover:underline">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  if (!campaign) {
    return null
  }

  return (
    <div className="relative">
      {/* Back Link */}
      <div className="mb-6">
        <Link
          to="/collaborations"
          className="text-gray-300 hover:text-white inline-flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Collaborations
        </Link>
      </div>

      {/* Header Section */}
      <CollaborationHeader campaign={campaign} />

      {/* Action Banner */}
      {statusDisplay && (
        <div className="mb-6">
          <ActionBanner
            title={statusDisplay.title}
            description={statusDisplay.description}
            actionButton={statusDisplay.actionButton}
            onAction={handleActionBannerClick}
            variant={statusDisplay.variant}
          />
        </div>
      )}

      {/* Collaboration Status - Always Visible */}
      <div className="mb-6">
        <CollaborationStatusCard
          campaign={campaign}
          detailLoading={detailLoading}
          hasVideoSuggestion={hasVideoSuggestion}
          hasConceptSuggestion={hasConceptSuggestion}
        />
      </div>

      {/* Payment */}
      {campaign.paymentAmount > 0 && isLiveAndPublishedComplete && (
        <div className="mb-6">
          <PaymentInfoCard campaign={campaign} campaignId={campaign.id} />
        </div>
      )}

      {/* Tabbed Content */}
      <div ref={tabsRef}>
        <CollaborationDetailTabs
          campaign={campaign}
          feedbackMessages={feedbackMessages}
          timeline={campaign.timeline || []}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          highlightEventId={highlightEventId}
        />
      </div>
    </div>
  )
}

export default CollaborationDetail
